import {
  pickAerialImageUrl,
  type AerialVideoUris,
} from "@/lib/integrations/aerial-view";
import { MAP_PIN_GOOGLE, MAP_PIN_STATIC } from "@/lib/virtualspace/map-brand";

/**
 * Location imagery helpers — Google Maps Static + Streetview Static URL
 * builders + a server-side Streetview Metadata probe.
 *
 * Why this lives here
 * -------------------
 * The public survey page (`/survey/[token]`), the survey gallery, the
 * PDF report cover, and the operator-side detail drawer all need to
 * answer the same question: *"is this the right place?"* with a small
 * picture next to an address. Before this helper, each surface
 * hand-rolled its own URL string with subtly different parameters
 * (size, FOV, scale) and had different fallback behaviour for the
 * "Google has no Street View at this address" case. Centralising the
 * builders here:
 *
 *   - keeps URL-shaped query strings in one auditable spot
 *   - means one place to bump `scale=2` or change FOV
 *   - lets every caller share the metadata probe so we don't render the
 *     generic "no imagery" placeholder when Google has no coverage
 *
 * Mobile-RAM discipline (per .cursorrules Decision #51)
 * -----------------------------------------------------
 * The survey cover card serves a field tech who may have 40+ photos
 * compressing in a web worker, the AI Coach holding a chat history,
 * and IndexedDB upload queues. We deliberately ship ONE static `<img>`
 * (~200KB decoded), not an interactive Street View panorama (~200KB JS
 * + WebGL canvas + PanoramaService). The static route is the right
 * primitive: zero JS, single fetch, fully cacheable at the CDN.
 *
 * Address-string vs lat/lng
 * --------------------------
 * Google Static APIs accept BOTH `?location=lat,lng` and
 * `?location=<urlencoded address>` — the latter triggers a server-side
 * geocode at Google. We prefer lat/lng when available (one fewer
 * geocode hop, deterministic) and fall back to the address string when
 * coords are missing. Bare address strings work on locations imported
 * via CSV without geocoding, which is the common "no coords" case.
 */

/**
 * Output dimensions + retina-scale factor. Defaults are tuned for the
 * survey cover card (~600x180 effective pixels). Scale=2 doubles the
 * effective resolution by serving a 2x asset that the browser scales
 * down — same bandwidth cost as a single-density image of the same
 * apparent size on a retina display.
 */
export interface ImageSizing {
  width?: number;
  height?: number;
  /** 1 (standard) | 2 (retina). Static APIs reject other values. */
  scale?: 1 | 2;
}

/**
 * Common location input — either lat/lng OR a free-form address
 * string. Callers with both should pass coords (preferred) AND
 * address (used as the URL fallback when coords are missing).
 */
export interface LocationInput {
  lat?: number | null;
  lng?: number | null;
  address?: string | null;
}

/**
 * Resolve the `location=` query-string value Google's Static APIs
 * accept. Returns null when neither coords nor a usable address is
 * present — callers should bail and render text-only in that case.
 */
function resolveLocationParam(input: LocationInput): string | null {
  if (
    input.lat != null &&
    input.lng != null &&
    Number.isFinite(input.lat) &&
    Number.isFinite(input.lng)
  ) {
    return `${input.lat},${input.lng}`;
  }
  if (input.address && input.address.trim()) {
    return encodeURIComponent(input.address.trim());
  }
  return null;
}

/**
 * Build a Google Streetview Static API URL for the front of a building.
 *
 * Returns null when there's neither coords nor an address (since both
 * fallbacks fail) OR when the maps key is missing — caller should
 * skip rendering rather than emit a broken `<img>`.
 *
 * The static endpoint always returns 200 OK with a generic gray "No
 * imagery available" placeholder when Google has no coverage at the
 * resolved location. Pair this with `checkStreetViewAvailability` to
 * avoid showing the placeholder; that endpoint is free and tells you
 * up-front whether real imagery exists.
 */
export function buildStreetViewStaticUrl(
  input: LocationInput & { mapsKey: string | null | undefined } & ImageSizing,
): string | null {
  const loc = resolveLocationParam(input);
  if (!loc || !input.mapsKey) return null;
  const w = input.width ?? 600;
  const h = input.height ?? 240;
  const scale = input.scale ?? 2;
  // FOV 85 = relatively wide angle so the building's full facade fits.
  // Pitch 0 = horizontal (eye-level). Heading is omitted so Google
  // picks the closest panorama and orients toward the address.
  return (
    `https://maps.googleapis.com/maps/api/streetview` +
    `?size=${w}x${h}` +
    `&scale=${scale}` +
    `&location=${loc}` +
    `&fov=85` +
    `&pitch=0` +
    `&key=${input.mapsKey}`
  );
}

/**
 * Build a Google Static Maps satellite URL with a marker pinning the
 * location. Returns null when neither coords nor address resolve OR
 * when the maps key is missing.
 *
 * The marker is critical for the multi-survey-per-day use case: a
 * top-down satellite view of two suburban buildings can look almost
 * identical, but the marker makes "this exact lot" unambiguous.
 */
export function buildSatelliteStaticUrl(
  input: LocationInput & {
    mapsKey: string | null | undefined;
    /** Map zoom (Google scale: ~17 = building-scale, ~14 = neighbourhood). */
    zoom?: number;
  } & ImageSizing,
): string | null {
  const loc = resolveLocationParam(input);
  if (!loc || !input.mapsKey) return null;
  const w = input.width ?? 600;
  const h = input.height ?? 240;
  const scale = input.scale ?? 2;
  const zoom = input.zoom ?? 18;
  // The `markers=color:0xC20000|<location>` parameter accepts the same
  // shape as `center` — lat/lng OR address — so we reuse loc for both.
  // The marker is the brand pin fill (map-brand.ts, AD #694).
  return (
    `https://maps.googleapis.com/maps/api/staticmap` +
    `?center=${loc}` +
    `&zoom=${zoom}` +
    `&size=${w}x${h}` +
    `&scale=${scale}` +
    `&maptype=satellite` +
    `&markers=color:${MAP_PIN_GOOGLE}%7C${loc}` +
    `&key=${input.mapsKey}`
  );
}

/**
 * Build a Mapbox Static Images API satellite URL — the SAME style +
 * overlay pattern the field-visit cards use via
 * `<StaticMapThumbnail>` (mapbox/satellite-v9, brand pin-fill
 * `pin-l+${MAP_PIN_STATIC}` marker, `@2x` retina). Centralised here so the public survey hero
 * and any future server-side caller share one auditable URL builder
 * instead of duplicating the component's string.
 *
 * Coords-only by design: Mapbox static images take `{lng},{lat}` —
 * there's no server-side geocode fallback like Google's `?center=
 * <address>`. Callers with an address but no coordinates should fall
 * back to `buildSatelliteStaticUrl` (Google) which accepts the bare
 * address string.
 *
 * Returns null when coords are missing/non-finite or the token is
 * absent — callers skip rendering rather than emit a broken `<img>`.
 */
export function buildMapboxSatelliteStaticUrl(input: {
  lat?: number | null;
  lng?: number | null;
  token: string | null | undefined;
  width?: number;
  height?: number;
  /** Mapbox zoom (~17-18 = building-scale). */
  zoom?: number;
  /** Overlay the VS-red pin at the point. Default true. */
  showPin?: boolean;
}): string | null {
  const { lat, lng } = input;
  if (
    lat == null ||
    lng == null ||
    !Number.isFinite(lat) ||
    !Number.isFinite(lng)
  ) {
    return null;
  }
  if (!input.token) return null;
  // Mapbox's Static Images API caps each dimension at 1280px before
  // the @2x multiplier — oversized requests come back as a bodyless
  // 422 (broken image). Clamp exactly like <StaticMapThumbnail>.
  const safeWidth = Math.max(1, Math.min(1280, Math.round(input.width ?? 600)));
  const safeHeight = Math.max(
    1,
    Math.min(1280, Math.round(input.height ?? 240)),
  );
  const zoom = input.zoom ?? 17;
  const showPin = input.showPin ?? true;
  // Marker overlay must precede the center/zoom fragment.
  const overlaySegment = showPin
    ? `/pin-l+${MAP_PIN_STATIC}(${lng},${lat})`
    : ``;
  return (
    `https://api.mapbox.com/styles/v1/mapbox/satellite-v9/static` +
    `${overlaySegment}/` +
    `${lng},${lat},${zoom},0/` +
    `${safeWidth}x${safeHeight}@2x` +
    `?access_token=${input.token}`
  );
}

/**
 * Mapbox Static Images — auto-fit bounds for multiple pin overlays.
 * Used for multi-site SpaceManager program headers (5+ sites). Caps pin
 * count to keep URL length sane. Defaults to satellite; program headers
 * pass a lighter overview style so national footprints stay readable.
 */
export function buildMapboxSatelliteStaticAutoUrl(input: {
  pins: readonly { lat: number; lng: number }[];
  token: string | null | undefined;
  width?: number;
  height?: number;
  maxPins?: number;
  /** Mapbox style id, e.g. `mapbox/light-v11`. Default satellite. */
  styleId?: string;
  /** Viewport padding in px (Mapbox Static `padding`). */
  padding?: number;
}): string | null {
  if (!input.token) return null;
  const maxPins = input.maxPins ?? 50;
  const pins = input.pins
    .filter(
      (p) =>
        Number.isFinite(p.lat) &&
        Number.isFinite(p.lng),
    )
    .slice(0, maxPins);
  if (pins.length === 0) return null;

  const safeWidth = Math.max(1, Math.min(1280, Math.round(input.width ?? 1280)));
  const safeHeight = Math.max(
    1,
    Math.min(1280, Math.round(input.height ?? 548)),
  );
  const styleId = (input.styleId ?? "mapbox/satellite-v9").replace(
    /^mapbox:\/\//,
    "",
  );
  const overlay = pins
    .map((p) => `pin-s+${MAP_PIN_STATIC}(${p.lng},${p.lat})`)
    .join(",");
  const padding =
    input.padding != null && Number.isFinite(input.padding)
      ? Math.max(0, Math.min(200, Math.round(input.padding)))
      : null;
  const paddingQuery = padding != null ? `&padding=${padding}` : "";
  return (
    `https://api.mapbox.com/styles/v1/${styleId}/static/` +
    `${overlay}/auto/${safeWidth}x${safeHeight}@2x` +
    `?access_token=${input.token}${paddingQuery}`
  );
}

/**
 * Stable key for deduping location rows that share one physical site.
 * Prefer rounded lat/lng (~1 m), then place_id, then normalized address.
 */
export function physicalSiteKey(input: {
  latitude?: number | null;
  longitude?: number | null;
  place_id?: string | null;
  address_line_1?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
}): string | null {
  const lat =
    input.latitude != null ? Number(input.latitude) : Number.NaN;
  const lng =
    input.longitude != null ? Number(input.longitude) : Number.NaN;
  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    return `geo:${lat.toFixed(5)},${lng.toFixed(5)}`;
  }
  const placeId = input.place_id?.trim();
  if (placeId) return `place:${placeId}`;
  const address = [
    input.address_line_1,
    input.city,
    input.state,
    input.postal_code,
  ]
    .filter(Boolean)
    .join("|")
    .trim()
    .toLowerCase();
  return address ? `addr:${address}` : null;
}

/**
 * Every landscape still we could show for a location, best first: Google's
 * Aerial View IMAGE thumbnail when the render is active, then a Mapbox
 * satellite tile, then a Google satellite tile. Callers that render an
 * `<img>` should walk this list on error instead of giving up on the first
 * source — a transient Mapbox 429 must not become a grey box.
 */
export function buildLocationAerialHeaderCandidates(input: {
  lat?: number | null;
  lng?: number | null;
  address?: string | null;
  aerialVideoState?: string | null;
  aerialVideoUris?: AerialVideoUris | null;
  mapsKey?: string | null;
  mapboxToken?: string | null;
  width?: number;
  height?: number;
  /** Mapbox zoom for the satellite fallback (default 17.5 ≈ building scale). */
  zoom?: number;
  /**
   * Skip the short-lived Aerial View IMAGE (signed URLs expire in
   * ~15–30 minutes). Home program cards use this so a cached still
   * cannot be the only `<img src>`.
   */
  omitAerialImage?: boolean;
}): string[] {
  const out: string[] = [];
  const aerialImage =
    input.omitAerialImage
      ? null
      : input.aerialVideoUris
        ? pickAerialImageUrl(input.aerialVideoUris, "landscape")
        : null;
  if (aerialImage) out.push(aerialImage);

  const width = input.width ?? 640;
  const height = input.height ?? 264;
  const mapbox = buildMapboxSatelliteStaticUrl({
    lat: input.lat,
    lng: input.lng,
    token: input.mapboxToken,
    width,
    height,
    zoom: input.zoom ?? 17.5,
    showPin: false,
  });
  if (mapbox) out.push(mapbox);
  const google = buildSatelliteStaticUrl({
    lat: input.lat,
    lng: input.lng,
    address: input.address,
    mapsKey: input.mapsKey,
    width,
    height,
    // Google zoom runs ~0.5 tighter than Mapbox at the same number.
    zoom: Math.round((input.zoom ?? 17.5) + 0.5),
  });
  if (google) out.push(google);
  return out;
}

/**
 * Landscape header still for a location card — the best candidate from
 * {@link buildLocationAerialHeaderCandidates}, or null when none applies.
 */
export function buildLocationAerialHeaderUrl(
  input: Parameters<typeof buildLocationAerialHeaderCandidates>[0],
): string | null {
  return buildLocationAerialHeaderCandidates(input)[0] ?? null;
}

/**
 * Probe the free Google Streetview Metadata API to find out whether
 * actual Street View imagery exists at the supplied location BEFORE
 * we render the static `<img>`. The static URL itself always returns
 * 200 (with a placeholder asset when no imagery exists) so without
 * this probe we'd silently show a generic "no imagery here" gray tile
 * to the spatialist — worse than just showing the satellite.
 *
 * The metadata endpoint is free per Google's documentation
 * (https://developers.google.com/maps/documentation/streetview/metadata#pricing)
 * so we can call it on every survey-page render without hitting cost.
 *
 * Returns true on `OK`, false on every other status (`ZERO_RESULTS`,
 * `NOT_FOUND`, `INVALID_REQUEST`, `OVER_QUERY_LIMIT`, network error,
 * timeout). The "always false on error" stance is conservative: when
 * we can't tell for sure, fall back to satellite rather than risk
 * showing the gray placeholder.
 *
 * Hard-capped at 4 seconds so a hung Google endpoint can't add 30s+
 * to the survey-page server-render time.
 */
export async function checkStreetViewAvailability(
  input: LocationInput & { mapsKey: string | null | undefined },
): Promise<boolean> {
  const loc = resolveLocationParam(input);
  if (!loc || !input.mapsKey) return false;

  const url =
    `https://maps.googleapis.com/maps/api/streetview/metadata` +
    `?location=${loc}` +
    `&key=${input.mapsKey}`;

  // AbortController gives us a hard deadline. Some hosts (corp DNS,
  // Iran/China censoring routes) can hang the TCP connect for
  // minutes — without the abort, the survey page would block on it.
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 4000);

  try {
    const res = await fetch(url, {
      signal: controller.signal,
      // Don't send any cookies/credentials. Pure public read.
      cache: "no-store",
    });
    if (!res.ok) return false;
    const json = (await res.json()) as { status?: string };
    return json.status === "OK";
  } catch {
    // Timeout, network error, JSON parse failure — all → false.
    return false;
  } finally {
    clearTimeout(timeout);
  }
}
